import { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { ekycRepository } from '../../../lib/repositories/ekyc.repository';
import { MOT_TRANG, NHIP_HOI, SO_NGAY_THONG_KE } from '../constants';
import { coPhienDangChay, dangChay, trangThaiCuaTab } from '../nghiep-vu/ekyc';
import type { AnhDaTai, PhienTomTat, TabHoSo, ThamDinhGui, ThongTinGiayTo, Trang, VaiTroTaiLen, VaiTroTep } from '../types';

/** Khoá cache: mọi thứ của module nằm dưới `['ekyc']`, vô hiệu hoá theo nhánh. */
export const KHOA_EKYC = {
  goc: ['ekyc'] as const,
  thongKe: ['ekyc', 'thong-ke'] as const,
  trienKhai: ['ekyc', 'trien-khai'] as const,
  toanVen: ['ekyc', 'toan-ven'] as const,
  phien: ['ekyc', 'phien'] as const,
  phienLoc: (tab: TabHoSo, tim: string) => ['ekyc', 'phien', 'ds', tab, tim] as const,
  chiTiet: (id: string) => ['ekyc', 'phien', 'chi-tiet', id] as const,
  nhatKy: (id: string) => ['ekyc', 'phien', 'nhat-ky', id] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

export function useThongKe() {
  return useQuery({
    queryKey: KHOA_EKYC.thongKe,
    queryFn: () => ekycRepository.thongKe(SO_NGAY_THONG_KE),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Chế độ triển khai chỉ đổi khi service khởi động lại, nên giữ lâu — hỏi lại mỗi lần vào màn là
 * một lời gọi không bao giờ trả lời khác đi.
 */
export function useTrienKhai() {
  return useQuery({
    queryKey: KHOA_EKYC.trienKhai,
    queryFn: () => ekycRepository.trienKhai(),
    staleTime: 600_000,
    retry: thuLai,
  });
}

/**
 * Toàn vẹn nhật ký.
 *
 * ⚠️ Ở v3 đường này luôn 403 (gateway thiếu nhánh `audit`). `retry: thuLai` **không** thử lại 4xx,
 * nên thẻ đi thẳng vào nhánh "chưa kiểm được" thay vì quay ba vòng rồi mới chịu.
 */
export function useToanVenNhatKy() {
  return useQuery({
    queryKey: KHOA_EKYC.toanVen,
    queryFn: () => ekycRepository.toanVenNhatKy(),
    staleTime: 300_000,
    retry: thuLai,
  });
}

/**
 * Danh sách hồ sơ định danh, phân trang cursor.
 *
 * `refetchInterval` **chỉ bật khi còn phiên đang chạy** — hết thì dừng. Hỏi vòng vĩnh viễn trên
 * một danh sách đã đứng yên chỉ đốt pin của người dùng và hạn mức của server.
 */
export function useDanhSachPhien(tab: TabHoSo, tim: string) {
  const query = useInfiniteQuery<
    Trang<PhienTomTat>,
    ApiError,
    InfiniteData<Trang<PhienTomTat>, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: KHOA_EKYC.phienLoc(tab, tim),
    queryFn: ({ pageParam }) =>
      ekycRepository.danhSach({
        limit: MOT_TRANG,
        cursor: pageParam,
        trangThai: trangThaiCuaTab(tab),
        ...(tim ? { search: tim } : {}),
      }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: thuLai,
    refetchInterval: (q) => {
      const dong = q.state.data?.pages.flatMap((p) => p.items) ?? [];
      return coPhienDangChay(dong) ? NHIP_HOI : false;
    },
  });

  return { ...query, dong: query.data?.pages.flatMap((p) => p.items) ?? [] };
}

/** Chi tiết một phiên; cũng chỉ hỏi lại khi phiên còn đang chạy. */
export function usePhien(id: string) {
  return useQuery({
    queryKey: KHOA_EKYC.chiTiet(id),
    queryFn: () => ekycRepository.chiTiet(id),
    staleTime: 15_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data && dangChay(q.state.data.status) ? NHIP_HOI : false),
  });
}

/**
 * Nhật ký của phiên.
 *
 * Hỏi lại được: nhật ký là dữ liệu **đã ghi**, đọc nó không tạo thêm dòng nào. Khác hẳn
 * `useAnhPhien` và `useThongTinGiayTo` ngay dưới.
 */
export function useNhatKy(id: string) {
  return useQuery({
    queryKey: KHOA_EKYC.nhatKy(id),
    queryFn: () => ekycRepository.nhatKy(id),
    staleTime: 15_000,
    retry: thuLai,
  });
}

/** Trạng thái của một lần mở dữ liệu theo yêu cầu. */
export interface MoTheoYeuCau<T> {
  duLieu: T | null;
  dangTai: boolean;
  loi: ApiError | null;
  /** Đã bấm mở ít nhất một lần — để UI biết chuyển từ nút sang vùng nội dung. */
  daMo: boolean;
}

/**
 * Mở dữ liệu giấy tờ (PII) **theo yêu cầu**.
 *
 * ⚠️ **State tay, KHÔNG react-query** — và đây không phải chuyện phong cách.
 *
 * Mỗi lời gọi `document-info` **ghi một dòng nhật ký kèm danh tính người xem** (`02` §7.1). Bọc nó
 * trong `useQuery` nghĩa là mọi lần refetch nền (đổi tab trình duyệt, mạng nối lại, cache hết hạn)
 * **bịa ra một dòng "đã xem dữ liệu cá nhân"** mà không ai bấm gì — làm hỏng đúng thứ nhật ký này
 * tồn tại để ghi (`06` §3.5).
 */
export function useThongTinGiayTo(id: string): MoTheoYeuCau<ThongTinGiayTo> & { mo: () => void } {
  const [duLieu, setDuLieu] = useState<ThongTinGiayTo | null>(null);
  const [dangTai, setDangTai] = useState(false);
  const [loi, setLoi] = useState<ApiError | null>(null);
  const [daMo, setDaMo] = useState(false);
  const dangChayRef = useRef(false);

  const mo = useCallback(() => {
    if (dangChayRef.current) return;
    dangChayRef.current = true;
    setDaMo(true);
    setDangTai(true);
    setLoi(null);
    ekycRepository
      .thongTinGiayTo(id)
      .then((d) => setDuLieu(d))
      .catch((e: ApiError) => setLoi(e))
      .finally(() => {
        setDangTai(false);
        dangChayRef.current = false;
      });
  }, [id]);

  return { duLieu, dangTai, loi, daMo, mo };
}

/**
 * Mở một ảnh của phiên **theo yêu cầu**, cùng lý do như trên.
 *
 * `URL.revokeObjectURL` lúc unmount và trước mỗi lần tải mới: mỗi object URL giữ cả Blob trong bộ
 * nhớ tới khi tab đóng, và một màn thẩm định mở qua lại ba vai trò ảnh sẽ rò dần.
 */
export function useAnhPhien(id: string): {
  anh: Partial<Record<VaiTroTep, string>>;
  dangTai: VaiTroTep | null;
  loi: Partial<Record<VaiTroTep, ApiError>>;
  mo: (vaiTro: VaiTroTep) => void;
} {
  const [anh, setAnh] = useState<Partial<Record<VaiTroTep, string>>>({});
  const [dangTai, setDangTai] = useState<VaiTroTep | null>(null);
  const [loi, setLoi] = useState<Partial<Record<VaiTroTep, ApiError>>>({});
  const urlRef = useRef<string[]>([]);

  useEffect(
    () => () => {
      for (const url of urlRef.current) URL.revokeObjectURL(url);
      urlRef.current = [];
    },
    [],
  );

  const mo = useCallback(
    (vaiTro: VaiTroTep) => {
      setDangTai(vaiTro);
      setLoi((cu) => {
        const moi = { ...cu };
        delete moi[vaiTro];
        return moi;
      });
      ekycRepository
        .anh(id, vaiTro)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          urlRef.current.push(url);
          setAnh((cu) => ({ ...cu, [vaiTro]: url }));
        })
        .catch((e: ApiError) => setLoi((cu) => ({ ...cu, [vaiTro]: e })))
        .finally(() => setDangTai(null));
    },
    [id],
  );

  return { anh, dangTai, loi, mo };
}

// ----- Ghi: luồng định danh tại quầy ------------------------------------------------------------------------

/**
 * Phiên bản điều khoản mà khách đọc trên màn hình.
 *
 * Ghi vào bản ghi đồng ý để trả lời được "người này đã đồng ý với văn bản NÀO". Đổi câu chữ ở `ekyc.dongY.*`
 * của catalog mà không tăng số này là bản ghi đồng ý trỏ vào một văn bản không còn tồn tại.
 */
export const PHIEN_BAN_DIEU_KHOAN = 'dinh-danh-tai-quay-v1';

/**
 * Mở phiên rồi ghi ngay câu trả lời của khách.
 *
 * Hai lời gọi, không một: đồng ý là một **hành vi của người dùng cuối**, ghi ở đường riêng có IP và thiết bị của
 * người bấm — ở màn này là khách đang đứng tại quầy, tự bấm trên màn hình. Mở phiên rồi chờ đó là để có chỗ ghi
 * câu trả lời, kể cả khi câu trả lời là "không".
 */
export function useBatDauDinhDanh() {
  return useMutation<PhienTomTat, ApiError, {
    documentType: string; referenceId: string; dongY: boolean; ungDungId?: string; workflowId?: string;
  }>({
    mutationFn: async ({ dongY, ...than }) => {
      const phien = await ekycRepository.taoPhien(than);
      return ekycRepository.dongY(phien.id, dongY, PHIEN_BAN_DIEU_KHOAN);
    },
  });
}

/** Tải một ảnh rồi gắn vào phiên. Hai bước ở server để ảnh qua cổng kiểm **một lần** rồi mới thuộc về phiên. */
export function useTaiAnh(phienId: string) {
  return useMutation<AnhDaTai, ApiError, { vaiTro: VaiTroTaiLen; tep: File }>({
    mutationFn: async ({ vaiTro, tep }) => {
      const anh = await ekycRepository.taiAnh(vaiTro, tep);
      await ekycRepository.ganAnh(phienId, anh.fileId, vaiTro);
      return anh;
    },
  });
}

/** Nộp hoặc huỷ: cả hai đổi danh sách và số liệu, nên làm mới mọi thứ dưới `['ekyc']`. */
export function useKetThucPhien(phienId: string) {
  const qc = useQueryClient();
  const lamMoi = () => void qc.invalidateQueries({ queryKey: KHOA_EKYC.goc });
  return {
    nop: useMutation<PhienTomTat, ApiError, void>({ mutationFn: () => ekycRepository.nop(phienId), onSuccess: lamMoi }),
    huy: useMutation<PhienTomTat, ApiError, void>({ mutationFn: () => ekycRepository.huy(phienId), onSuccess: lamMoi }),
  };
}

/**
 * Huỷ hoặc xoá dữ liệu cá nhân của **một hồ sơ bất kỳ** — cho menu thao tác trên dòng của danh sách, nơi id chỉ có
 * lúc bấm. Cùng việc với `useKetThucPhien` nhưng không gắn sẵn một phiên.
 *
 * Không thử lại khi lỗi: xoá dữ liệu cá nhân là thao tác không hoàn tác, gửi lại vì mạng chập chờn thì người bấm
 * không còn biết mình đã xoá mấy lần.
 */
export function useThaoTacHoSo() {
  const qc = useQueryClient();
  const lamMoi = () => void qc.invalidateQueries({ queryKey: KHOA_EKYC.goc });
  return {
    huy: useMutation<PhienTomTat, ApiError, string>({ mutationFn: (id) => ekycRepository.huy(id), onSuccess: lamMoi }),
    xoa: useMutation<void, ApiError, string>({ mutationFn: (id) => ekycRepository.xoa(id), onSuccess: lamMoi }),
  };
}

/**
 * Quyết định thẩm định. Làm mới mọi thứ dưới `['ekyc']`: trạng thái, số liệu tổng quan và nhật ký đều đổi theo.
 * Không thử lại khi lỗi: một quyết định về một con người không được gửi hai lần vì mạng chập chờn.
 */
export function useThamDinh(phienId: string) {
  const qc = useQueryClient();
  return useMutation<PhienTomTat, ApiError, ThamDinhGui>({
    mutationFn: (du) => ekycRepository.thamDinh(phienId, du),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KHOA_EKYC.goc }),
  });
}
